import { type WorkflowsNode } from 'generated/resources/graphql'
import { z } from 'zod'

import {
  BackendActionNode,
  BackendNode,
  BackendNodeData,
  BackendStartrNode,
  BackendSwitchNode,
  BackendTerminateNode,
  isBackendSwitchNodeData,
} from './backend-types'
import { deserializeExpression, serializeExpression } from './conditionAst/codec'

function safeJsonParse(input: string): unknown {
  try {
    return JSON.parse(input)
  } catch {
    return undefined
  }
}

const RawSwitchNodeDataSchema = z.object({
  _type: z.literal('switch_data'),
  comparisons: z.array(
    z.object({
      label: z.string(),
      priority: z.number(),
      expression: z.unknown(),
    })
  ),
})

const hasComparisons = (data: unknown): data is { comparisons: unknown[] } => {
  if (typeof data !== 'object' || data === null) return false
  if (!('comparisons' in data)) return false
  return Array.isArray(data.comparisons)
}

const RawActionNodeDataSchema = z.object({
  payload: z.record(z.unknown()),
  handler_name: z.string(),
})

export const deserializeWorkflowNodes = (nodes: WorkflowsNode[]): BackendNode[] => {
  return nodes.map((node) => {
    const rawData = node.data ? safeJsonParse(node.data) : undefined

    switch (node.nodeType) {
      case 'switch': {
        // Handle switch nodes that might not have data yet
        if (!rawData || !hasComparisons(rawData) || rawData.comparisons.length === 0) {
          // Return a default empty switch node
          return {
            ...node,
            nodeType: 'switch',
            data: {
              _type: 'switch_data',
              comparisons: [],
            },
          } satisfies BackendSwitchNode
        }

        const parsed = RawSwitchNodeDataSchema.safeParse(rawData)
        if (!parsed.success) {
          throw new Error(
            `Workflows: invalid data for node type 'switch' (id=${node.id}, identifier=${node.identifier}), ${parsed.error.message}`
          )
        }

        // Deserialize all comparisons with their expressions
        const comparisons = parsed.data.comparisons.map((comparison) => ({
          label: comparison.label,
          priority: comparison.priority,
          expression: deserializeExpression(comparison.expression),
        }))

        return {
          ...node,
          nodeType: 'switch',
          data: {
            _type: 'switch_data',
            comparisons,
          },
        } satisfies BackendSwitchNode
      }
      case 'action': {
        const parsed = RawActionNodeDataSchema.safeParse(rawData)
        if (!parsed.success) {
          throw new Error(
            `Workflows: invalid data for node type 'action' (id=${node.id}, identifier=${node.identifier}), ${parsed.error.message}`
          )
        }
        return {
          ...node,
          nodeType: 'action',
          data: {
            _type: 'action_data',
            ...parsed.data,
          },
        } satisfies BackendActionNode
      }
      case 'start': {
        const triggerNode: BackendStartrNode = {
          ...node,
          nodeType: 'start',
          data: {},
        }
        return triggerNode
      }
      case 'terminate': {
        const terminateNode: BackendTerminateNode = {
          ...node,
          nodeType: 'terminate',
          data: {},
        }
        return terminateNode
      }
      default: {
        throw new Error(
          `Workflows: unsupported node type '${node.nodeType}' (id=${node.id}, identifier=${node.identifier})`
        )
      }
    }
  })
}

export const serializeNodeData = (data: BackendNodeData): string | null => {
  // early return if data is empty
  if (Object.keys(data).length === 0) {
    return null
  }

  // Handle expression serialization for switch nodes
  if (isBackendSwitchNodeData(data)) {
    const serializedData = {
      ...data,
      comparisons: data.comparisons.map((comparison) => ({
        label: comparison.label,
        priority: comparison.priority,
        expression: serializeExpression(comparison.expression),
      })),
    }
    return JSON.stringify(serializedData)
  }

  return JSON.stringify(data)
}
